import { captureRemixErrorBoundaryError, withSentry, type SentryMetaArgs } from "@sentry/remix";
import { type LoaderFunctionArgs, json, type LinksFunction, createCookie } from "@remix-run/node";
import {
	isRouteErrorResponse, Links, Meta, Outlet, redirect, Scripts,
	ScrollRestoration, useNavigation, useRouteError, useRouteLoaderData
} from "@remix-run/react";
import styles from "./_styles/tailwind.css?url";
import mainStyles from "./_styles/main.scss?url";
import themeStyles from "./_styles/theme.scss?url";
import { Navbar } from "./components/Navbar";
import { authenticator } from "./services/auth.server";
import { getUserPrefs } from "./services/user-prefs.server";
import { IMAGES_HOST } from "./services/constants.server";
import { NewCache } from "./cache/NewCache.server";
import { useTranslation } from "react-i18next";
import { useChangeLanguage } from "remix-i18next/react"
import { logger } from "./services/logger.server";
import { Footer } from "./components/Footer";
import { Cta } from "./components/Cta";
import { supportedLngs } from "~/i18n"
import { sessionStorage } from "./services/session.server";
import { getFingerprint } from "./services/fingerprint.server";
import { SiteCache } from "./cache/SiteCache.server";
import { getCssAssets, getFavIconAssets, getIconAsset } from "./services/site-assets";
import { VoucherPlacement, type Jwt } from "@client/api-services/client";
import { useGtm } from "./components/hooks/useGtm";
import { useZendesk } from "./components/hooks/useZendesk";
import { LoaderComponent } from "./components/common/LoaderComponent";
import { RemixFetch } from "./apis/remix-fetch.server";
import { setSentryUser } from "./apis/utils.server";
import { parseAcceptLanguage } from "intl-parse-accept-language";

async function autherizeUsingOldCookie(request: Request, headers: Headers) {
	const oldCookieName = "eTopupOnline.Identity=";
	//document.cookie = "eTopupOnline.Identity=CfDJ8HkHkGD1qndAkSm4vPpmhea8T4ODLMpVAr7eKjQE3pf_Zh1VL7xjkpIoSMt7c6bCbUtj5XhqFGsbMWCclrgGcVElA85oytWLCV9rYAhCecZnL5cxEKJCDs21cTSl9fDAcs7CZd_4t4M6ult6gxHyzejerSNNxPwoy5S3yMormJbYWAhm7IyRzpAhcUMIMze6dlotW7jw59zWYzEMaahav7Mnzi1Of8l7KmH8-OeBzjK5G-w3VxYxQ_qYrCRgUiXLiCMO8elowz3Rh2F2HcCX8iVCKpyLl4fZV-XNWcZU_KPWeBJrV8qKd0rE3sv7OdP6AovRjl0gV0a7yYbF2IXemuu7SGcZaB5WdcZ0zDNOP6rindRwrRParmiRJ43ENnnQFCeuV-WuxN5ARxfzvpcP2tdggKp2RULGndC5ASsgJz0a-zbtQOplXBnV7IDlqep6KGWxPUpVRZFWUnHwA33oIXcmTYgv5YWiY61ipdzNpuUkwOYrXq4P6yrHOuYZ0GA22adjaYRXHzrLtgKWwosSB6gE_k1wPea2SVY0jorLPLoUvdlyOOff0LJCnLDAXgfW2lHaT0xkPaJPxMI5VVi4bve7E_iUyxV9XT24aQDLoKKsKmiYq6ssehjKpwQJr6Z5_KNfeRggDxOMDJmo42CTRLihQtcvNLx2eG0b5NaxR2Xn; Path=/; Max-Age=3600";
	function getCookieValue() {
		const cookieHeader = request.headers.get("cookie") ?? "";
		const startIndex = cookieHeader.indexOf(oldCookieName);
		if (startIndex === -1) return null;
		const valueStartIndex = startIndex + oldCookieName.length;
		const endIndex = cookieHeader.indexOf(";", valueStartIndex);
		return endIndex === -1 ? cookieHeader.slice(valueStartIndex) : cookieHeader.slice(valueStartIndex, endIndex);
	}
	const cookieValue = getCookieValue();
	if (!cookieValue) return;
	logger.info("etoCookie", cookieValue);
	const remix = new RemixFetch(request, headers);
	try {
		const response = await remix.post<Jwt>("/api/auth/convert-old-cookie-to-new-jwt", {}, "json", {
			isAnonymous: true,
			headers: { "Cookie": `.AspNetCore.Identity.Application=${cookieValue}` }
		});
		if (response.isSuccessful) {
			const session = await sessionStorage.getSession(request.headers.get("cookie"))
			session.set("user", response.payLoad)
			headers.append("set-cookie", await sessionStorage.commitSession(session, { maxAge: undefined }));
		}
	} catch {
		//bail out
	} finally {
		headers.append("Set-Cookie", `${oldCookieName}; Path=/; Max-Age=0; HttpOnly`);
	}
	throw redirect(request.url, { headers });
}
function getLocale(request: Request) {
	const languages = parseAcceptLanguage(request.headers.get("accept-language"));
	if (!languages || languages.length === 0) return "en-US";
	return languages[0];
}
async function loaderInternal({ request, params }: LoaderFunctionArgs) {
	const headers = new Headers();
	await autherizeUsingOldCookie(request, headers);
	const userPrefs = await getUserPrefs(request, headers);
	const fingerPrint = await getFingerprint(request, headers);
	const language = supportedLngs.includes(params.lang as string) ? params.lang : "en";
	const locale = getLocale(request);
	const user = await authenticator.getUser(request);
	setSentryUser(request, user);
	const currencies = await NewCache.getCatlogCurrencies(request);
	const { trackerSettings, siteId, voucherPlacement, supportsVoucher, selfSettings } = await SiteCache.getSettings(request);
	const data = {
		user,
		language, locale,
		GTMAppId: trackerSettings.gtmAppId,
		env: {
			IMAGES_HOST: IMAGES_HOST,
			SENTRY_DSN: process.env.SENTRY_DSN,
			SENTRY_ENVIRONMENT: process.env.SENTRY_ENVIRONMENT,
			SENTRY_TRACES_SAMPLE_RATE: process.env.SENTRY_TRACES_SAMPLE_RATE,
			SENTRY_RELEASE: process.env.SENTRY_RELEASE,
		},
		supportedCurrencies: currencies,
		userPrefs,
		fingerprintTags: fingerPrint.fingerprintTags,
		siteId,
		selfSettings,
		showVoucherTab: supportsVoucher && (voucherPlacement === VoucherPlacement.Tab || voucherPlacement === VoucherPlacement.Both),
	};
	return json(data, { headers })
}
export async function loader(args: LoaderFunctionArgs) {
	try {
		return await loaderInternal(args);
	} catch (e) {
		if (e instanceof Response) throw e;
		logger.error(e, "Error occurred at root loader");
		throw e;
	}
}

export type RootData = ReturnType<typeof useRouteLoaderData<typeof loader>>

export function Layout({ children }: { children: React.ReactNode }) {
	const rootLoaderData = useRouteLoaderData("root") as RootData & { sentryTrace: string, sentryBaggage: string };
	useChangeLanguage(rootLoaderData?.language ?? "en");	//Very important!! Do not remove.
	useGtm(rootLoaderData?.GTMAppId);
	useZendesk();
	// useEffect(() => {
	// 	//@ts-ignore
	// 	void import("smartbanner.js");
	// }, []);
	const navigation = useNavigation();
	const { i18n } = useTranslation();
	if (!rootLoaderData) {
		return children;
	}
	const { language, GTMAppId, env, siteId, fingerprintTags = [], showVoucherTab = false, selfSettings } = rootLoaderData;

	const showLoader = navigation.state === "loading" || navigation.state === "submitting"
	const siteCss = getCssAssets(siteId);
	const vesta = fingerprintTags.find(f => f.serviceName === "Vesta");
	const riskified = fingerprintTags.find(f => f.serviceName === "Riskified")
	const favIconUrl = getFavIconAssets(siteId);
	return (
		<html lang={language} dir={i18n.dir()}>
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=2.0" />
				<meta name="format-detection" content="telephone=no, date=no, email=no, address=no" />
				<meta name="sentryTrace" content={rootLoaderData.sentryTrace} />
				<meta name="sentryBaggage" content={rootLoaderData.sentryBaggage} />
				{/* <meta name="apple-itunes-app" content="app-id=1239445975" />
				<link rel="manifest" href="/manifest.json" /> */}
				{/* Start SmartBanner configuration */}
				{/* <meta name="smartbanner:title" content="Smart Application" />
				<meta name="smartbanner:author" content="SmartBanner Contributors" />
				<meta name="smartbanner:price" content="FREE" />
				<meta name="smartbanner:price-suffix-apple" content=" - On the App Store" />
				<meta name="smartbanner:price-suffix-google" content=" - In Google Play" />
				<meta name="smartbanner:icon-apple" content={getIconAsset(siteId)} />
				<meta name="smartbanner:icon-google" content={getIconAsset(siteId)} />
				<meta name="smartbanner:button" content="VIEW" />
				<meta name="smartbanner:button-url-apple" content="https://apps.apple.com/us/app/etopuponline-global-recharge/id1239445975" />
				<meta name="smartbanner:button-url-google" content="https://play.google.com/store/apps/details?id=com.etopuponline" />
				<meta name="smartbanner:enabled-platforms" content="android" />
				<meta name="smartbanner:close-label" content="Close" />
				<meta name="smartbanner:include-user-agent-regex" content="Chrome" /> */}
				{/* End SmartBanner configuration */}
				<noscript>
					<iframe src={`https://www.googletagmanager.com/ns.html?id=${GTMAppId}`} height="0" width="0"
						style={{ display: "none", visibility: "hidden" }}>
					</iframe>
				</noscript>
				<link rel="icon" href={favIconUrl} />
				<Meta />
				<Links />
				<link rel="stylesheet" href={siteCss} />
			</head>
			<body className="px-4 lg:px-24 grid grid-rows-[1fr_auto] min-h-screen">
				<div>
					<header className="nav-bar-container sticky top-0 z-10 -mx-4 px-4 lg:-mx-24 lg:px-24 bg-white">
						<Navbar siteId={siteId} showVoucherTab={showVoucherTab} />
					</header>
					<main className="my-6 mt-8">
						{children}
					</main>
				</div>
				<div>
					<section className="my-6">
						<Cta />
					</section>
					<footer className="my-6">
						<Footer siteId={siteId} selfSettings={selfSettings} />
					</footer>
				</div>
				{showLoader ? <LoaderComponent delay={100} /> : null}
				<ScrollRestoration />
				<Scripts />
				{vesta ? <script src={`/scripts/vesta-fingerprint.js?tags=${encodeURIComponent(JSON.stringify(vesta))}`} async></script> : null}
				{riskified?.fingerPrintUrl ? <script async type="text/javascript" src={riskified?.fingerPrintUrl}></script> : null}
				{vesta ? <script async src="/scripts/vestatoken-1.0.3.js"></script> : null}
				<script dangerouslySetInnerHTML={{ __html: `window.ENV = ${JSON.stringify(env)};` }} />
			</body>
		</html>
	);
}

function App() {
	return <Outlet />;
}
export default withSentry(App);

export const handle = { i18n: ["translation", "category", "error"] };

export const links: LinksFunction = () => {
	return [
		{ rel: "stylesheet", href: styles },
		{ rel: "stylesheet", href: mainStyles },
		{ rel: "stylesheet", href: themeStyles }
	];
};
// export const meta: MetaFunction = () => {
// 	return [
// 		{ title: "Your Store Name | Quality Products" },
// 		{ name: "description", content: "Shop the latest products - Your trusted online store" },
// 		// Open Graph tags
// 		{ property: "og:title", content: "Your Store Name | Quality Products" },
// 		{ property: "og:description", content: "Shop the latest products - Your trusted online store" },
// 		{ property: "og:image", content: "/images/store-logo.jpg" },
// 		{ property: "og:url", content: "https://yourstore.com" },
// 	];
// };
export function ErrorBoundary() {
	//TODO: production error page
	const error = useRouteError();
	captureRemixErrorBoundaryError(error);
	if (isRouteErrorResponse(error)) {
		return (
			<div>
				<h1>Custom error</h1>
				<h2>
					{error.status} {error.statusText}
				</h2>
				<p>{error.data?.message}</p>
			</div>
		);
	} else if (error instanceof Error) {
		return (
			<div>
				<h1>{error.name}</h1>
				<p>{error.message}</p>
				{error.stack ? <>
					<p>The stack trace is:</p>
					<pre>{error.stack}</pre>
				</> : null}
			</div>
		);
	} else {
		return <div>
			<h1>Unknown error occured.</h1>
			{error ? <p>{`${error}`}</p> : null}
		</div>
	}
}